import { PhoneIncoming } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { PartyLabel } from '@/components/PartyLabel'
import { Button } from '@/components/ui/button'
import type { IncomingCall } from '@/models/realtime'

const LEAD_FALLBACK = 'Unknown lead'
const CUSTOMER_FALLBACK = 'Unknown customer'

// The banner reads "contact name (company name)"; when the contact is unknown we
// show just the company, and when the company is blank, just the fallback.
function partyText(contactName: string | null, company: string, fallback: string): string {
  if (contactName) return company ? `${contactName} (${company})` : contactName
  return company || fallback
}

function more(count: number, kind: string): string {
  return count > 1 ? ` and ${count - 1} other ${kind}${count > 2 ? 's' : ''}` : ''
}

/**
 * What a screen reader announces for a ringing call: every party the caller
 * matched, since one number can belong to a lead and a customer at once.
 */
export function callerLabel(call: IncomingCall): string {
  const parts: string[] = []
  if (call.lead)
    parts.push(
      partyText(call.lead.contactName, call.lead.legalName, LEAD_FALLBACK) + more(call.matchCount, 'lead')
    )
  if (call.customer)
    parts.push(
      partyText(call.customer.contactName, call.customer.name, CUSTOMER_FALLBACK) +
        more(call.customerMatchCount, 'customer')
    )
  return parts.join(' and ') || call.fromNumber
}

// A shared number (a dispatch line, an owner running several carriers) matches
// more than one record. We link the most recently updated one, so say so rather
// than presenting that guess as the only answer.
function MoreMatches({ count, kind }: { count: number; kind: string }) {
  if (count <= 1) return null
  return (
    <span
      className="text-muted-foreground shrink-0 text-xs"
      title={`This number matches ${count} ${kind}s — showing the most recently updated`}
    >
      +{count - 1}
    </span>
  )
}

// One matched party in the banner: who they are + where to open them.
function CallerMatch({
  contactName,
  company,
  fallback,
  matchCount,
  kind,
  action,
}: {
  contactName: string | null
  company: string
  fallback: string
  matchCount: number
  kind: string
  action: ReactNode
}) {
  return (
    <>
      <span className="truncate text-sm font-medium">
        <PartyLabel contactName={contactName} company={company} fallback={fallback} />
      </span>
      <MoreMatches count={matchCount} kind={kind} />
      {action}
    </>
  )
}

/**
 * Incoming-call notification: who's ringing + how to open them. Fed by the
 * webhook-backed SSE stream (reliable) and accelerated by the dialer widget's
 * client-side ringing event (instant, but only the caller number). A number can
 * belong to a lead and to a customer at once, so both render when both match.
 */
export function IncomingCallBanner({
  call,
  onShowLead,
}: {
  call: IncomingCall
  onShowLead: (leadId: string) => void
}) {
  const { lead, customer } = call
  return (
    // No `mr-auto`: the bar is full-width under a higher-z sidebar, so a
    // left-pinned banner hides behind it. Cluster on the right with the buttons.
    <div className="flex max-w-[min(60vw,44rem)] min-w-0 items-center gap-2">
      <PhoneIncoming className="size-4 shrink-0 animate-pulse text-green-600 motion-reduce:animate-none" />
      {lead && (
        <CallerMatch
          contactName={lead.contactName}
          company={lead.legalName}
          fallback={LEAD_FALLBACK}
          matchCount={call.matchCount}
          kind="lead"
          action={
            <Button
              variant="outline"
              size="sm"
              className="h-8 shrink-0 text-xs"
              onClick={() => onShowLead(lead.id)}
            >
              Show lead
            </Button>
          }
        />
      )}
      {lead && customer && <span className="text-muted-foreground shrink-0 text-sm">·</span>}
      {customer && (
        <CallerMatch
          contactName={customer.contactName}
          company={customer.name}
          fallback={CUSTOMER_FALLBACK}
          matchCount={call.customerMatchCount}
          kind="customer"
          action={
            <Button asChild variant="outline" size="sm" className="h-8 shrink-0 text-xs">
              <Link to={`/insure/internal/customers/${customer.id}`}>View customer</Link>
            </Button>
          }
        />
      )}
      {!lead && !customer && (
        <span className="truncate text-sm font-medium">
          Incoming call · {/* Unknown caller: click the number to search leads for it. */}
          <Link
            to={`/insure/internal/sales-leads?search=${encodeURIComponent(call.fromNumber)}`}
            className="hover:text-ink underline underline-offset-2"
          >
            {call.fromNumber}
          </Link>
        </span>
      )}
    </div>
  )
}
